import { Trans, useTranslation } from '@jq4race/i18n';
import { Button } from '@jq4race/ui';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface PaginationProps {
  total: number;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
}

export function Pagination({ total, page, pageSize, onPageChange }: PaginationProps) {
  const { t } = useTranslation('users');
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(page, pageCount);
  const from = total === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const to = Math.min(safePage * pageSize, total);

  if (total <= pageSize) {
    return (
      <p className="px-1 text-[12px] text-jq-graphite">
        {total === 0 ? t('pagination.noResults') : t('pagination.result', { count: total })}
      </p>
    );
  }

  return (
    <div className="flex items-center justify-between gap-3">
      <p className="text-[12px] text-jq-graphite">
        <Trans
          t={t}
          i18nKey="pagination.showing"
          values={{ from, to, total }}
          components={{ strong: <span className="font-medium text-jq-charcoal" /> }}
        />
      </p>
      <div className="flex items-center gap-1">
        <Button
          size="sm"
          variant="ghost"
          onClick={() => onPageChange(safePage - 1)}
          disabled={safePage <= 1}
          aria-label={t('pagination.previousPage')}
        >
          <ChevronLeft className="h-4 w-4" strokeWidth={1.5} />
        </Button>
        <span className="min-w-[64px] text-center text-[12px] font-medium text-jq-graphite">
          {safePage} / {pageCount}
        </span>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => onPageChange(safePage + 1)}
          disabled={safePage >= pageCount}
          aria-label={t('pagination.nextPage')}
        >
          <ChevronRight className="h-4 w-4" strokeWidth={1.5} />
        </Button>
      </div>
    </div>
  );
}
